import React, { useState } from "react";
import { useTeamTiers, isTeamTierTeam } from "../services/TeamTierService";
import { TeamTierBadge } from "./Badges/TeamTierBadge";

interface TeamLogoProps {
    logo?: string | null;
    name?: string | null;
    className?: string;
    style?: React.CSSProperties;
    alt?: string;
    /**
     * Pass the team's id to show the team-donator badge when the team holds the tier. Omitted
     * everywhere the badge isn't wanted, which keeps the markup byte-identical at those call sites.
     */
    teamId?: string | null;
}

// Renders a team's logo, falling back to a deterministic initials avatar when there is no logo
// (e.g. donator-uploaded pug/scrim teams) or when the logo URL fails to load. The fallback is an
// inline SVG that scales to whatever box the caller's className/style defines, so it looks correct
// in the match header, the scoreboard and the detailed cards without per-call sizing.
const TeamLogo: React.FC<TeamLogoProps> = ({ logo, name, className, style, alt, teamId }) => {
    const [errored, setErrored] = useState(false);
    const tiers = useTeamTiers();
    const label = alt ?? name ?? "Team";
    const showTier = isTeamTierTeam(teamId, tiers);

    const image = logo && !errored
        ? <img src={logo} alt={label} className={showTier ? undefined : className} style={showTier ? { width: "100%", height: "100%", objectFit: "contain" } : style} onError={() => setErrored(true)} />
        : null;

    // Without a badge the markup is exactly what it always was — no wrapper, no layout shift at any
    // of the existing call sites.
    if (image && !showTier) return image;

    const initials =
        (name || "")
            .trim()
            .split(/\s+/)
            .map((w) => w[0])
            .filter(Boolean)
            .slice(0, 2)
            .join("")
            .toUpperCase() || "?";

    // Deterministic, pleasant hue from the name so the two teams read as distinct.
    let hash = 0;
    for (const ch of name || "") hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
    const bg = `hsl(${hash % 360}, 42%, 38%)`;

    const fallback = (
        <svg viewBox="0 0 64 64" width="100%" height="100%" style={{ display: "block" }} role="img" aria-hidden="true">
            <rect width="64" height="64" rx="14" fill={bg} />
            <text x="32" y="33" dominantBaseline="central" textAnchor="middle" fontFamily="sans-serif" fontWeight="700" fontSize="30" fill="#ffffff">
                {initials}
            </text>
        </svg>
    );

    if (showTier) {
        return (
            <span
                className={className}
                style={{ ...style, position: "relative", display: "inline-flex", overflow: "visible" }}
                aria-label={label}
                title={label}
            >
                {image ?? fallback}
                <TeamTierBadge teamName={name} corner />
            </span>
        );
    }

    return (
        <span className={className} style={{ ...style, display: "inline-flex", overflow: "hidden" }} aria-label={label} title={label}>
            {fallback}
        </span>
    );
};

export default TeamLogo;
